/* eslint-disable jsdoc/require-param-description */
/* eslint-disable jsdoc/require-param-type */
/* eslint-disable jsdoc/check-param-names */
import React, { useContext, useState, useEffect, useMemo } from "react";
import { supabase } from "../supabase";
import { useAuth } from "./AuthContext";
import { ERROR_CODES } from "../components/shared/constants";

// Create a context to hold company-related data
const CompanyContext = React.createContext();

/**
 * Hook to access the company context, which holds data such as product types and suppliers.
 *
 * @returns {object} The company context, which includes the following properties:
 * - productTypes: an array of product types
 * - activeProductTypes: an array of active product types
 * - suppliers: an array of suppliers
 */
export function useCompany() {
  return useContext(CompanyContext);
}

/**
 * The CompanyProvider component provides company data to its children components,
 * such as product types, suppliers, products, dealer levels and lifecycle statuses.
 * It also provides functions to upsert (insert or update) a product type, supplier,
 * product and dealer level, and re-fetches the data from the database when the
 * user subscribes to changes in the database.
 *
 * @param children.children
 * @param children The children components that will receive the company data
 * @returns A CompanyContext.Provider component that wraps the children
 * components and provides them with the company data
 */
export function CompanyProvider({ children }) {
  // State variables to hold various data sets for the company
  const [productTypes, setProductTypes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [activeSuppliers, setActiveSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [lifecycleStatuses, setLifecycleStatuses] = useState([]);
  const [dealTypes, setDealTypes] = useState([]);
  const [updateFreeze, setUpdateFreeze] = useState(true);
  const [leaderLevels, setLeaderLevels] = useState([]);
  const [productTypesSuppliers, setProductTypesSuppliers] = useState([]);

  const { currentUser } = useAuth();

  /**
   * Fetches all product types from the database, orders them by name and stores
   * them in the state.
   *
   * @throws {Error} If the database query fails
   */
  const fetchProductTypes = async () => {
    const fields = "id, name";

    if (currentUser.is_admin) {
      const { data, error } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("refs_product_type")
        .select(fields)
        .eq("is_deleted", false);

      if (error) {
        throw error;
      } else {
        setProductTypes(data);
      }
    } else {
      const { data, error } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("users_product_type_index")
        .select(`refs_product_type(${fields})`)
        .eq("user_id", currentUser.id)
        .eq("refs_product_type.is_deleted", false);

      if (error) {
        throw error;
      } else {
        const productTypeMap = data
          .filter(({ refs_product_type }) => refs_product_type !== null)
          .map(productType => {
            return productType.refs_product_type;
          });
        setProductTypes(productTypeMap);
      }
    }
  };

  const upsertProductType = async productType => {
    await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("refs_product_type")
      .upsert(productType);

    // Re-fetch products and product types to refresh data
    fetchProductTypes();
  };

  // Function to fetch products from the database and set state
  const fetchProducts = async () => {
    const { data, error } = await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("products_master")
      .select("*")
      .order("refs_product_status_id", { ascending: false })
      .order("display_name", { ascending: true });

    if (error) {
      throw error;
    } else {
      setProducts(data);
    }
  };

  // Function to fetch deal_types from the database and set state
  const fetchDealTypes = async () => {
    const { data, error } = await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("refs_deal_type")
      .select("*");

    if (error) {
      throw error;
    } else {
      setDealTypes(data);
    }
  };

  // Fetch a specific product by its ID
  const getProduct = async id => {
    const { data, error } = await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("products_master")
      .select("*")
      .eq("id", id);
    if (error) {
      throw error;
    } else return data[0];
  };

  // Upsert a product and re-calculate its business logic
  const upsertProduct = async product => {
    const { data, error } = await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("products_master")
      .upsert(product)
      .select();

    if (error) {
      console.error(error);
      return { error: error.message, data: [] };
    }

    fetchProducts();
    return { error: 0, data };
  };

  // Function to fetch suppliers and set state
  const fetchSuppliers = async () => {
    const fields = "*, refs_supplier_status(*)";

    if (currentUser.is_admin) {
      const { data, error } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("suppliers_master")
        .select(fields)
        .eq("is_deleted", false)
        .order("supplier_status")
        .order("supplier_name");
      if (error) {
        throw error;
      } else {
        setSuppliers(data);

        /* supplier_status:
          1: Active
          2: Testing
          3: Cancelled
        */

        setActiveSuppliers(
          data.filter(
            supplier =>
              supplier.supplier_status === 1 || supplier.supplier_status === 2
          )
        );
      }
    } else {
      const { data, error } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("users_supplier_index")
        .select(`suppliers_master(${fields})`)
        .eq("suppliers_master.is_deleted", false)
        .eq("user_id", currentUser.id)
        .order("supplier_status", {
          referencedTable: "suppliers_master",
          ascending: true,
        })
        .order("supplier_name", {
          referencedTable: "suppliers_master",
          ascending: true,
        });

      if (error) {
        throw error;
      } else {
        const suppliersMap = data
          .filter(({ suppliers_master }) => suppliers_master !== null)
          .map(supplier => supplier.suppliers_master);

        setSuppliers(suppliersMap);

        /* supplier_status:
        1: Active
        2: Testing
        3: Cancelled
        */
        setActiveSuppliers(
          suppliersMap.filter(
            supplier =>
              supplier.supplier_status === 1 || supplier.supplier_status === 2
          )
        );
      }
    }
  };

  // Fetch a specific supplier by ID
  const getSupplier = async id => {
    const { data, error } = await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("suppliers_master")
      .select("*")
      .eq("id", id);
    if (error) {
      throw error;
    } else return data[0];
  };

  // Upsert a supplier and re-calculate associated products
  const upsertSupplier = async supplier => {
    const { data, error } = await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("suppliers_master")
      .upsert(supplier)
      .select();

    if (error) {
      console.error(error);
      return { data: [], error: ERROR_CODES.DB_INDEX_CLEAR_FAILED };
    }
    fetchSuppliers();
    return { data: data[0], error: 0 };
  };

  // Function to fetch lifecycle statuses of dealers
  const fetchLifecycleStatuses = async () => {
    const { data, error } = await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("refs_user_lifecycle_status")
      .select("*");

    if (error) {
      console.error(error);
      throw error;
    } else {
      setLifecycleStatuses(data);
    }
  };

  /**
   * Fetches the settings stored in the database and updates the state of the updateFreeze setting
   */
  async function fetchSettings() {
    const { data, error } = await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("metadata")
      .select("*");

    if (error) {
      console.error(error.message);
    } else if (data[0]) {
      // Extract updateFreeze setting and update state
      const updateFreezeSettings = data.filter(
        set => set.name === "updateFreeze"
      )[0].settings;

      const { updateFreeze: updateFreezeSB } = updateFreezeSettings;

      setUpdateFreeze(updateFreezeSB);
    }
  }

  /**
   * Toggles the state of the updateFreeze setting in the database.
   *
   * This function asynchronously updates the "updateFreeze" setting in the "settings" table
   * by flipping its current boolean value. It targets the record with id 1.
   * Logs any errors encountered during the update process to the console.
   */
  async function toggleFreeze() {
    const { error } = await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("metadata")
      .update({ settings: { updateFreeze: !updateFreeze } })
      .eq("id", 1);

    if (error) console.error(error.message);
    fetchSettings();
  }

  const fetchLeaderSubStatuses = async () => {
    const { data, error } = await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("refs_structure_level")
      .select("*");

    if (error) {
      throw error;
    } else {
      setLeaderLevels(data);
    }
  };

  const fetchProductTypesSuppliers = async () => {
    const { data, error } = await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("suppliers_product_type_index_view")
      .select("*");

    if (error) {
      throw error;
    } else {
      setProductTypesSuppliers(data);
    }
  };

  // Effect hook to fetch data when the component is mounted and subscribe to database changes

  useEffect(() => {
    const unsubscribe = () => {
      fetchProductTypes();
      fetchProducts();
      fetchSuppliers();
      fetchLifecycleStatuses();
      fetchSettings();
      fetchLeaderSubStatuses();
      fetchProductTypesSuppliers();
      fetchDealTypes();
    };

    return unsubscribe();
  }, []);

  useEffect(() => {
    if (currentUser.id) {
      fetchProductTypes();
      fetchSuppliers();
    }
  }, [currentUser.id]);

  // Memoize the value to avoid unnecessary re-renders
  const value = {
    productTypes,
    suppliers,
    activeSuppliers,
    products,
    upsertProductType,
    getSupplier,
    upsertSupplier,
    getProduct,
    upsertProduct,
    lifecycleStatuses,
    updateFreeze,
    toggleFreeze,
    leaderLevels,
    productTypesSuppliers,
    dealTypes,
  };

  const memoizedValue = useMemo(() => value, [value]);

  // Provide the company data to the children components
  return (
    <CompanyContext.Provider value={memoizedValue}>
      {children}
    </CompanyContext.Provider>
  );
}
